import Vue from "vue";
import { ApolloClient, ApolloLink, HttpLink, InMemoryCache } from "apollo-boost";
import { onError } from "apollo-link-error";
import VueApollo from "vue-apollo";
import store from "../store";
import { getApiUrl } from "@/helpers";

Vue.use(VueApollo);

// Use getApiUrl which handles Electron detection internally
const graphqlUri = getApiUrl('/graphql');

/**
 * Gets the print token from URL query parameters if present.
 * Used for auto-print pages that need to authenticate GraphQL requests.
 */
function getPrintTokenFromUrl(): string | null {
	const urlParams = new URLSearchParams(window.location.search);
	return urlParams.get('printToken');
}

const httpLink = new HttpLink({
	uri: graphqlUri,
	includeExtensions: true,
	credentials: 'include',
});

const errorLink = onError(({ networkError, graphQLErrors }) => {
	// @ts-ignore
	if (networkError?.statusCode === 401) {
		// Don't redirect to login for print token auth failures
		const printToken = getPrintTokenFromUrl();
		if (!printToken) {
			store.dispatch("logout");
			if (!location.href.includes("/login")) location.href = "/login";
		}
	}
});

const acceptJsonMiddleware = new ApolloLink((operation, forward) => {
	operation.extensions = {
		...operation.extensions,
		location: window.location.href,
	};

	// Build headers, including print token if present
	const headers: Record<string, string> = {
		"access-control-allow-origin": "*",
		"content-type": "application/json; charset=utf-8",
	};

	const printToken = getPrintTokenFromUrl();
	if (printToken) {
		headers["X-Print-Token"] = printToken;
	}

	operation.setContext({
		headers,
		uri: graphqlUri + ( operation.operationName ? `/${operation.operationName}` : ""),
	});
	return forward(operation);
});

export function createProvider() {
	const apolloClient = new ApolloClient({
		link: ApolloLink.from([errorLink, acceptJsonMiddleware, httpLink]),
		cache: new InMemoryCache({
			addTypename: true,
		}),
	});

	const apolloProvider = new VueApollo({
		defaultClient: apolloClient,
		defaultOptions: {
			$query: {
				fetchPolicy: "no-cache",
			},
		},
	});

	return apolloProvider;
}
